import { Badge, Box, Button, HStack, Popover, PopoverBody, PopoverContent, PopoverTrigger, Portal, Separator, Skeleton, useDisclosure, VStack } from "@chakra-ui/react";
import React, { useMemo, useRef, useState } from "react";
import FaceliftText from "@/components/Atoms/FaceliftText";
import { PopupDetailsGrid, PopupField } from "@/components/Molecules/Panel/Popover";
import Show from "@/components/Molecules/Show";

export type RowItem = {
  id: string;
  title: string;
  name: string;
  /** Clinic/account name shown as its own line */
  clinic?: string;
  subtitleLeft?: string;
  subtitleRight?: string;
  status?: { label: string; colorScheme: string };
  meta?: string;
  amount?: string;
  popoverData?: { label: string; value: string | number | React.ReactNode }[]; // for dynamic popover content
};


// ====== UI Pieces ======
const StatusPill = ({
  label,
  colorScheme,
}: {
  label: string;
  colorScheme: string;
}) => {
  return (
    <Box
      px="3.5"
      py="1"
      borderRadius="7px"
      style={{
        backgroundColor: colorScheme
      }}
      color={'white'}
      textTransform="none"
      fontSize="xs"
      lineHeight="1"
      textAlign="center"
    >
      {label}
    </Box>
  );
}

const ListRowSkeleton = () => {
  return (
    <Box px="3" py="3" borderRadius="md">
      <HStack align="start" gap="1">
        <VStack align="start" gap="2" flex="1">
          <Skeleton height="14px" width="120px" borderRadius="6px" />
          <Skeleton height="14px" width="160px" borderRadius="6px" />
          <Skeleton height="12px" width="220px" borderRadius="6px" />
        </VStack>

        <VStack align="end" ml={-10} gap="2">
          <Skeleton height="22px" width="72px" borderRadius="7px" />
          <Skeleton height="10px" width="24px" borderRadius="6px" />
        </VStack>
      </HStack>
    </Box>
  );
}

const clamp = (n: number, min: number, max: number) => {
  return Math.max(min, Math.min(max, n));
}

const POPUP_W = 520;
const GAP = 12;
const VIEWPORT_PAD = 12;

const ListRow = ({ item }: { item: RowItem }) => {
  const rowRef = useRef<HTMLDivElement | null>(null);

  const [open, setOpen] = useState(false);
  const [hoveringPopup, setHoveringPopup] = useState(false);
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });

  const computePosition = () => {
    const el = rowRef.current;
    if (!el) return;

    const r = el.getBoundingClientRect();

    const vw = window.innerWidth;
    const vh = window.innerHeight;

    // available space on each side of the row
    const spaceRight = vw - r.right - VIEWPORT_PAD;
    const spaceLeft = r.left - VIEWPORT_PAD;

    // Prefer right, else left. Never bottom.
    let left: number;

    if (spaceRight >= POPUP_W + GAP) {
      // open to the RIGHT of the row
      left = r.right + GAP;
    } else if (spaceLeft >= POPUP_W + GAP) {
      // open to the LEFT of the row
      left = r.left - POPUP_W - GAP;
    } else {
      // fallback: choose the side with more room and clamp inside viewport
      const preferRight = spaceRight >= spaceLeft;
      left = preferRight ? r.right + GAP : r.left - POPUP_W - GAP;
      left = clamp(left, VIEWPORT_PAD, vw - POPUP_W - VIEWPORT_PAD);
    }

    // Vertically align popup with the row (not below)
    // This keeps scrolling clear and feels like a "hover card"
    const preferredTop = r.top + r.height / 2 - 180; // 180 ~= half of popup visual height (tweak if needed)
    const top = clamp(preferredTop, VIEWPORT_PAD, vh - 380); // 380 ~= max popup height guard (tweak)

    setPos({ top, left });
  };

  const onEnterRow = () => {
    computePosition();
    setOpen(true);
  };

  const onLeaveRow = () => {
    // small delay so user can move into popup
    setTimeout(() => {
      setOpen((prev: boolean) => (hoveringPopup ? prev : false));
    }, 120);
  };

  const showPopup = open || hoveringPopup;

  React.useEffect(() => {
    if (!open) return;

    const onMove = () => computePosition();

    window.addEventListener("scroll", onMove, true); // capture scroll from containers too
    window.addEventListener("resize", onMove);

    return () => {
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open]);

  return (
    <>
      {/* ✅ Your row */}
      <Box
        ref={rowRef}
        px="3"
        py="3"
        borderRadius="md"
        transition="background 150ms ease"
        _hover={{
          _dark: { bg: "gray.500" },
          _light: { bg: "gray.100" },
        }}
        onMouseEnter={onEnterRow}
        onMouseLeave={onLeaveRow}
      >
        <HStack align="start" gap="3">
          <VStack align="start" gap="1" flex="1">
            <FaceliftText fontWeight="600" fontSize="sm" lineHeight="1.1">
              {item.title}
            </FaceliftText>

            {item.amount ? (
              <FaceliftText fontSize="sm" fontWeight="600" lineHeight="1.1">
                {item.amount}
              </FaceliftText>
            ) : (
              <FaceliftText fontSize="sm" fontWeight="600" lineHeight="1.1">
                {item.name}
              </FaceliftText>
            )}

            <Show when={!!item.clinic}>
              <FaceliftText fontSize="sm" lineHeight="1.1" color={{ _dark: "gray.300", _light: "gray.600" }}>
                🏥 {item.clinic}
              </FaceliftText>
            </Show>

            <HStack gap="2" color="gray.600" fontSize="sm" lineHeight="1.1" wrap="wrap">
              <Show when={!!item.subtitleLeft}>
                <FaceliftText color={{ _dark: "gray.300", _light: "gray.500" }}>
                  {item.subtitleLeft}
                </FaceliftText>
              </Show>

              <Show when={!!item.subtitleRight}>
                <>
                  {item.subtitleLeft ? (
                    <FaceliftText color={{ _dark: "gray.300", _light: "gray.500" }} />
                  ) : null}
                  <FaceliftText color={{ _dark: "gray.300", _light: "gray.500" }}>
                    {item.subtitleRight}
                  </FaceliftText>
                </>
              </Show>
            </HStack>
          </VStack>

          <VStack align="end" gap="2" minW="90px">
            <Show when={!!item.status}>
              <StatusPill
                label={item?.status?.label || ""}
                colorScheme={item?.status?.colorScheme || ""}
              />
            </Show>
            <Show when={!!item.meta}>
              <FaceliftText fontSize="xs" color="gray.500" fontWeight="500">
                {item.meta}
              </FaceliftText>
            </Show>
          </VStack>
        </HStack>
      </Box>

      {/* ✅ Hover popup (Box only) */}
      <Portal>
        {showPopup && item.popoverData ? (
          <Box
            position="fixed"
            top={`${pos.top}px`}
            left={`${pos.left}px`}
            w={`${POPUP_W}px`}
            bg={{ _light: "white", _dark: "gray.800" }}
            borderWidth="1px"
            borderColor={{ _light: "gray.200", _dark: "gray.600" }}
            borderRadius="xl"
            boxShadow="xl"
            zIndex={2000}
            my={-5}
            onMouseEnter={() => setHoveringPopup(true)}
            onMouseLeave={() => {
              setHoveringPopup(false);
              setOpen(false);
            }}
            transition="all 150ms ease"
          >
            <Box p="6">
              <PopupDetailsGrid fields={item.popoverData} gap={1} columns={3} />
            </Box>
          </Box>
        ) : null}
      </Portal>
    </>
  );
};


const ListPanel = ({ items, isLoading }: { items: RowItem[]; isLoading: boolean }) => {
  const count = 6;

  return (
    <VStack gap="0" align="stretch">
      <Show when={isLoading}>
        {Array.from({ length: count }).map((_, idx) => (
          <React.Fragment key={`sk-${idx + 1}`}>
            <ListRowSkeleton />
            {idx !== count - 1 ? <Separator borderColor="gray.100" /> : null}
          </React.Fragment>
        ))}
      </Show>

      <Show when={!isLoading}>
        {items.map((it, idx) => (
          <React.Fragment key={`${it.id}-${idx}`}>
            <ListRow item={it} />
            {idx !== items.length - 1 ? <Separator borderColor="gray.600" /> : null}
          </React.Fragment>
        ))}
      </Show>
    </VStack>
  );
}

export default ListPanel;